/**
 * @file src/components/organisms/RiskBarDial/RiskBarDial.tsx
 * @description This file defines the RiskBarDial component for displaying risk scores with a dial.
 * @module RiskBarDial
 */

import React, { FC } from 'react';
import { ListItem } from '@/components';
import BarDial from '@/components/atoms/BarDial/BarDial';
import { YieldBearingAssetInterface } from '@/interfaces';

interface RiskBarDialProps {
  asset: YieldBearingAssetInterface;
  mode?: 'leftHalf' | 'rightHalf' | 'full';
  width?: string;
  height?: string;
}

const RiskBarDial: FC<RiskBarDialProps> = ({
  asset,
  mode = 'full',
  width = 'w-24',
  height = 'h-4',
}) => {
  const getRiskTypeColor = (
    score: number | undefined,
  ):
    | 'error'
    | 'primary'
    | 'accent'
    | 'secondary'
    | 'warning'
    | 'disabled'
    | null
    | undefined => {
    switch (score) {
      case 0:
      case 1:
        return 'accent';
      case 2:
        return 'primary';
      case 3:
      case 4:
        return 'error';
      default:
        return null;
    }
  };

  const riskTypeColor = getRiskTypeColor(asset.riskScore);

  return (
    <BarDial
      value={asset.riskScore ? (asset.riskScore / 4) * 100 : 0}
      mode={mode}
      fillColor="bg-error"
      width={width}
      height={height}
      showBreakpoints={true}
      hoverContent={
        <div className="flex flex-col items-start">
          <ListItem
            mainText={asset.riskType ? `${asset.riskType}` : 'Unknown'}
            mainTextColor={riskTypeColor}
            subSubText={'Risk Profile'}
          />
        </div>
      }
    />
  );
};

export default RiskBarDial;
